import { useCallback, useState } from 'react';

const clone = <T>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

export const useFormHistory = <T>(initial: T | null) => {
  const [past, setPast] = useState<T[]>([]);
  const [present, setPresent] = useState<T | null>(initial);
  const [future, setFuture] = useState<T[]>([]);

  const canUndo = past.length > 0;
  const canRedo = future.length > 0;

  const set = useCallback(
    (next: T) => {
      setPast((prev) => (present ? [...prev, clone(present)] : prev));
      setPresent(clone(next));
      setFuture([]);
    },
    [present],
  );

  const reset = useCallback((next: T) => {
    setPast([]);
    setPresent(clone(next));
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    if (!canUndo || present === null) return;
    const previous = past[past.length - 1];
    setPast((prev) => prev.slice(0, -1));
    setFuture((prev) => [clone(present), ...prev]);
    setPresent(clone(previous));
  }, [canUndo, past, present]);

  const redo = useCallback(() => {
    if (!canRedo || present === null) return;
    const next = future[0];
    setFuture((prev) => prev.slice(1));
    setPast((prev) => [...prev, clone(present)]);
    setPresent(clone(next));
  }, [canRedo, future, present]);

  return { past, present, future, canUndo, canRedo, set, reset, undo, redo };
};
